/**
 * Pure pricing math for BookingWidget, extracted to keep the widget small.
 *
 * Contract (preserved 1:1 from the original inline calculateTotal()):
 *   - When selectedPackage is set → returns the package-priced shape.
 *   - When check-in / check-out is missing → returns null.
 *   - Otherwise → computes nightly base, per-guest extra, length-of-stay
 *     discounts, Quebec taxes (14.975% QST + 3.5% lodging tax) and total.
 *
 * DO NOT change the tax rates, discount rounding, or field names returned by
 * this hook. The backend `pricing.py` mirrors these numbers and the Stripe
 * webhook stores them in session metadata — any drift causes visible
 * mismatches on receipts / earnings.
 */
import { useMemo } from 'react';

export default function usePropertyPricing({
  property,
  checkInDate,
  checkOutDate,
  guests,
  bookingType,
  selectedRoom,
  selectedPackage,
  promoDiscount = 0,
}) {
  return useMemo(() => {
    if (!property) return null;

    // --- Package booking shortcut ---
    if (selectedPackage) {
      return {
        nights: selectedPackage.duration_months * 30,
        pricePerNight: selectedPackage.monthly_payment / 30,
        accommodationSubtotal: selectedPackage.total_price,
        cleaningFee: 0,
        managementFee: 0,
        petFee: 0,
        communicationFee: 0,
        provincialTax: 0,
        cityTax: 0,
        totalTaxes: 0,
        total: selectedPackage.total_price,
        damageDeposit: property.damage_deposit || 0,
        isPackage: true,
        packageDuration: selectedPackage.duration_months,
        monthlyPayment: selectedPackage.monthly_payment,
      };
    }

    if (!checkInDate || !checkOutDate) return null;

    const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
    if (nights <= 0) return null;

    const totalGuests = guests.adults + guests.children;

    const extraGuestFee = parseFloat(property.extra_guest_fee) || 0;
    const guestsIncluded = parseInt(property.guests_included) || 1;

    let guestPricingObj = property.guest_pricing;
    if (typeof guestPricingObj === 'string') {
      try {
        guestPricingObj = JSON.parse(guestPricingObj);
      } catch {
        guestPricingObj = null;
      }
    }

    // Resolve per-night base. Single-room legacy fallback when no per-guest
    // pricing is configured on the property.
    let nightBase = parseFloat(property.price) || 0;
    if (
      bookingType === 'single_room' &&
      extraGuestFee === 0 &&
      (!guestPricingObj || !Object.keys(guestPricingObj).length)
    ) {
      if (selectedRoom && selectedRoom.price) nightBase = parseFloat(selectedRoom.price);
      else if (property.room_price) nightBase = parseFloat(property.room_price);
    }

    let pricePerNight;
    if (extraGuestFee > 0) {
      pricePerNight = nightBase + extraGuestFee * Math.max(0, totalGuests - guestsIncluded);
    } else if (guestPricingObj && Object.keys(guestPricingObj).length) {
      const legacy = parseFloat(
        guestPricingObj[totalGuests] || guestPricingObj[String(totalGuests)] || 0,
      );
      pricePerNight = legacy > 0 ? legacy : nightBase;
    } else {
      pricePerNight = nightBase;
    }
    pricePerNight = Math.round(pricePerNight * 100) / 100;

    const accommodationSubtotal = Math.round(pricePerNight * nights * 100) / 100;

    const cleaningFee = property.cleaning_fee || 50;
    const damageDeposit = property.damage_deposit || 0;
    const managementFee = parseFloat(property.management_fee) || 0;
    const petFee = parseFloat(property.pet_fee) || 0;
    const communicationFee = parseFloat(property.communication_fee) || 0;

    // --- Length-of-stay discounts ---
    const discount1Nights = property.discount_1_nights || 0;
    const discount1Percentage = property.discount_1_percentage || 0;
    const discount2Nights = property.discount_2_nights || 0;
    const discount2Percentage = property.discount_2_percentage || 0;

    let discountPercentage = 0;
    let discountLabel = '';
    if (discount2Nights > 0 && nights >= discount2Nights && discount2Percentage > 0) {
      discountPercentage = discount2Percentage;
      discountLabel = `${discount2Nights}+ night discount`;
    } else if (discount1Nights > 0 && nights >= discount1Nights && discount1Percentage > 0) {
      discountPercentage = discount1Percentage;
      discountLabel = `${discount1Nights}+ night discount`;
    }

    const discountAmount =
      discountPercentage > 0
        ? Math.round((accommodationSubtotal * discountPercentage / 100) * 100) / 100
        : 0;
    // Promo discount applies on top of any length-of-stay discount, and
    // crucially BEFORE taxes — Quebec QST/GST and Montreal lodging tax both
    // attach to the post-discount accommodation. Backend `payments.py`
    // mirrors this exact order; do not split it apart from the total here
    // or receipts will diverge from the Stripe charge.
    const safePromo = Math.max(0, Number(promoDiscount) || 0);
    const discountedAccommodation = Math.max(
      0,
      accommodationSubtotal - discountAmount - safePromo,
    );

    // --- Quebec taxes ---
    // Tax rule (Revenu Québec):
    //   • QST+GST 14.975% → full taxable amount (accommodation + cleaning +
    //     all fees). Parking is handled server-side and also taxed here.
    //   • Montreal lodging tax 3.5% → accommodation ONLY (not cleaning, not
    //     parking, not extras).
    const taxableAmount =
      discountedAccommodation + cleaningFee + managementFee + petFee + communicationFee;
    const provincialTaxRate = 0.14975;
    const cityTaxRate = 0.035;
    const provincialTaxBase = taxableAmount;
    const cityTaxBase = discountedAccommodation;
    const provincialTax = Math.round(provincialTaxBase * provincialTaxRate * 100) / 100;
    const cityTax = Math.round(cityTaxBase * cityTaxRate * 100) / 100;
    const totalTaxes = provincialTax + cityTax;
    const total =
      Math.round(
        (discountedAccommodation +
          cleaningFee +
          managementFee +
          petFee +
          communicationFee +
          totalTaxes) *
          100,
      ) / 100;

    return {
      nights,
      pricePerNight,
      accommodationSubtotal,
      nightlyBreakdown: [], // retained for API-parity
      discountAmount,
      discountPercentage,
      discountLabel,
      discountedAccommodation,
      cleaningFee,
      managementFee,
      petFee,
      communicationFee,
      provincialTax,
      provincialTaxRate,
      cityTax,
      cityTaxRate,
      totalTaxes,
      damageDeposit,
      total,
    };
  }, [selectedPackage, checkInDate, checkOutDate, guests, bookingType, selectedRoom, property, promoDiscount]);
}
